import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, AlertCircle } from "lucide-react";

const chf = (n) => Number(n || 0).toFixed(2);
const qty = (n) => Number(n || 0).toFixed(0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

function ByDistributor({ items }) {
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gaps-by-distributor">
      {items.map((d) => (
        <div key={d.distributor_id} className="bg-white border border-[#E5E5E0] rounded-xl p-4" data-testid={`gaps-dist-${d.distributor_id}`}>
          <div className="text-sm font-medium text-[#1A1C1A] truncate">{d.distributor_name}</div>
          <div className="font-heading text-2xl text-[#A04E2C]">CHF {chf(d.removed_cost)}</div>
          <div className="text-xs text-[#575A57]">
            {qty(d.removed_qty)} barq. retirées en écart · {d.events} réassort(s)
            {d.shortage_qty > 0 && <> · Nayax +{qty(d.shortage_qty)} signalé</>}
          </div>
        </div>
      ))}
    </div>
  );
}

function GapRow({ r, i }) {
  return (
    <tr className="border-t border-[#EEEEE8]" data-testid={`gap-row-${i}`}>
      <td className="p-3 whitespace-nowrap text-xs font-mono text-[#575A57]">{fmtDate(r.created_at)}</td>
      <td className="p-3">{r.distributor_name}</td>
      <td className="p-3">{r.product_name}</td>
      <td className="p-3 text-center font-mono">{r.slot_code}</td>
      <td className="p-3 text-right tabular-nums">{qty(r.am_before)}</td>
      <td className="p-3 text-right tabular-nums">{qty(r.nayax_qty)}</td>
      <td className="p-3 text-right tabular-nums font-medium text-[#A04E2C]">{r.removed_qty > 0 ? qty(r.removed_qty) : "—"}</td>
      <td className="p-3 text-right tabular-nums">{r.removed_qty > 0 ? chf(r.removed_cost) : "—"}</td>
      <td className="p-3 text-right tabular-nums text-[#8C6B2F]">{r.shortage_qty > 0 ? `+${qty(r.shortage_qty)}` : "—"}</td>
    </tr>
  );
}

export default function ReassortGaps({ distributors }) {
  const [distributorId, setDistributorId] = useState("");
  const [weeks, setWeeks] = useState("12");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ weeks });
      if (distributorId) params.set("distributor_id", distributorId);
      const { data } = await api.get(`/reports/reassort-gaps?${params}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [weeks, distributorId]);

  useEffect(() => { load(); }, [load]);
  const rows = data?.rows || [];

  return (
    <div className="space-y-5" data-testid="reassort-gaps-view">
      <div className="bg-white border border-[#E5E5E0] rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="mb-1.5 block text-xs">Distributeur</Label>
          <Select value={distributorId || "__ALL__"} onValueChange={(v) => setDistributorId(v === "__ALL__" ? "" : v)}>
            <SelectTrigger data-testid="gaps-filter-distributor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">Tous</SelectItem>
              {distributors.filter((d) => !d.archived).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Période</Label>
          <Select value={weeks} onValueChange={setWeeks}>
            <SelectTrigger data-testid="gaps-filter-weeks"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["4", "8", "12", "26", "52"].map((w) => <SelectItem key={w} value={w}>{w} dernières semaines</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-[#575A57] self-end">
          À chaque réassort validé, les anciens restes de l&apos;emplacement sont alignés sur le stock Nayax.
          Les barquettes en trop côté AgroManage sont listées ici, sans compter comme perte.
        </p>
      </div>

      {error && <div className="text-sm text-[#C05C47] flex gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
      {loading && <Loader2 className="w-5 h-5 animate-spin text-[#1F4E3D]" />}
      {data && (
        <div className="text-sm text-[#575A57]" data-testid="gaps-totals">
          Total : <b className="text-[#A04E2C]">{qty(data.totals.removed_qty)} barq. · CHF {chf(data.totals.removed_cost)}</b> au coût de revient
          {data.totals.shortage_qty > 0 && <> · Nayax en plus signalé : {qty(data.totals.shortage_qty)}</>}
        </div>
      )}
      <ByDistributor items={data?.by_distributor || []} />
      {data?.missing_cost_products?.length > 0 && (
        <p className="text-xs text-[#A04E2C]" data-testid="gaps-missing-cost">
          Coût de revient inconnu (compté à 0) : {data.missing_cost_products.join(", ")}
        </p>
      )}

      <div className="bg-white border border-[#E5E5E0] rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="gaps-table">
          <thead className="bg-[#F5F5F0] text-xs text-[#575A57]">
            <tr>
              <th className="text-left p-3">Réassort</th>
              <th className="text-left p-3">Distributeur</th>
              <th className="text-left p-3">Plat</th>
              <th className="text-center p-3">Empl.</th>
              <th className="text-right p-3">AgroManage</th>
              <th className="text-right p-3">Nayax</th>
              <th className="text-right p-3">Retirées</th>
              <th className="text-right p-3">CHF</th>
              <th className="text-right p-3">Nayax en plus</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} className="p-6 text-center text-[#575A57]">Aucun écart de réassort sur la période.</td></tr>
            )}
            {rows.map((r, i) => <GapRow key={r.id} r={r} i={i} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
